import React, { useRef, useState } from "react";

interface EmbedPlayerProps {
  src: string;
}

const formatTime = (secs: number) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
};

import { TMDB_API_KEY } from './tmdb-config';

// Extrai tipo (movie/tv) e id TMDB da URL
function parseTmdbFromUrl(pathname: string) {
  // Ex: /movie/672, /movie/672.mp4, /tv/1418/1/1, /tv/1418/1/1.mp4
  const movieMatch = pathname.match(/\/movie\/(\d+)(?:\.[^\/]+)?/);
  if (movieMatch) {
    return { type: 'movie', id: movieMatch[1] };
  }
  const tvMatch = pathname.match(/\/tv\/(\d+)(?:[\/]|\.[^\/]+|$)/);
  if (tvMatch) {
    return { type: 'tv', id: tvMatch[1] };
  }
  return null;
}

function ensureMp4Extension(url: string): string {
  // Se já tem extensão de vídeo, retorna igual
  if (/\.(mp4|webm|mkv|mov)(\?|$)/i.test(url)) return url;
  // Se tem query params, insere antes deles
  const [base, query] = url.split("?");
  return query ? `${base}.mp4?${query}` : `${base}.mp4`;
}

const EmbedPlayer: React.FC<EmbedPlayerProps> = ({ src }) => {
  // --- Progresso local ---
  const getBaseSrc = (src: string) => src.split('?')[0];
const CACHE_KEY = `playflix-embed-progress:${getBaseSrc(src)}`;
  const [resumeDialog, setResumeDialog] = React.useState<null | number>(null); // null = não mostrar, number = tempo salvo
  const [resumeAccepted, setResumeAccepted] = React.useState(false);

  // Ao montar: verifica se há progresso salvo
 

  // Log de renderização do componente e estados principais
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [buffer, setBuffer] = useState(0);
  const [showFeedback, setShowFeedback] = useState<null | 'rewind' | 'forward'>(null);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const playerContainerRef = React.useRef<HTMLDivElement>(null);
  const [movieTitle, setMovieTitle] = useState<string>('PlayFlix Player');
  const [posterPath, setPosterPath] = useState<string | null>(null);


  // Buscar dados do TMDB
  React.useEffect(() => {
    const { pathname } = window.location;
    const tmdbInfo = parseTmdbFromUrl(pathname);
    if (!tmdbInfo) {
      setMovieTitle('PlayFlix Player');
      setPosterPath(null);
      document.title = 'PlayFlix Player';
      return;
    }
    async function fetchTmdbData(info: { type: string; id: string }) {
      const endpoint = info.type === 'movie'
        ? `https://api.themoviedb.org/3/movie/${info.id}?api_key=${TMDB_API_KEY}&language=pt-BR`
        : `https://api.themoviedb.org/3/tv/${info.id}?api_key=${TMDB_API_KEY}&language=pt-BR`;
      try {
        const resp = await fetch(endpoint);
        if (!resp.ok) throw new Error('Erro ao buscar TMDB');
        const data = await resp.json();
        setMovieTitle(data.title || data.name || 'PlayFlix Player');
        setPosterPath(data.poster_path ? `https://image.tmdb.org/t/p/w780${data.poster_path}` : null);
        document.title = data.title || data.name || 'PlayFlix Player';
      } catch (e) {
        console.error('[TMDB] Erro ao buscar dados do TMDB:', e);
        setMovieTitle('PlayFlix Player');
        setPosterPath(null);
        document.title = 'PlayFlix Player';
      }
    }
    fetchTmdbData(tmdbInfo);
  }, []);

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      const saved = raw ? parseFloat(raw) : 0;
      if (saved && saved > 10) {
        setResumeDialog(saved);
      } else {
      }
    } catch (e) {
      console.warn('[CACHE] Erro ao ler progresso salvo:', e);
    }
  }, [src]);

  // Atualiza o cache a cada 5s ou ao pausar
  React.useEffect(() => {
    if (!resumeAccepted && resumeDialog !== null) {
      return;
    }
    if (!playing || seeking) return;
    const interval = setInterval(() => {
      if (videoRef.current && videoRef.current.currentTime > 10) {
        localStorage.setItem(CACHE_KEY, String(videoRef.current.currentTime));
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [playing, seeking, resumeAccepted, resumeDialog]);

  // Salva ao pausar
  React.useEffect(() => {
    if (!resumeAccepted && resumeDialog !== null) {
      return;
    }
    if (!playing && videoRef.current && videoRef.current.currentTime > 10) {
      localStorage.setItem(CACHE_KEY, String(videoRef.current.currentTime));
    }
  }, [playing, resumeAccepted, resumeDialog]);

  // Ao aceitar retomar
  React.useEffect(() => {
    if (resumeAccepted && resumeDialog && videoRef.current) {
      videoRef.current.currentTime = resumeDialog;
      setCurrent(resumeDialog);
      setResumeDialog(null);
      setTimeout(() => {
        videoRef.current?.play();
      }, 0);
    }
  }, [resumeAccepted, resumeDialog]);

  // Ao finalizar (95%+), limpa cache
  React.useEffect(() => {
    if (duration && current > duration * 0.95) {
      localStorage.removeItem(CACHE_KEY);
    }
  }, [current, duration]);

  // Sumir controles após 3s sem interação
  React.useEffect(() => {
    if (!controlsVisible) return;
    const timeout = setTimeout(() => setControlsVisible(false), 3000);
    return () => clearTimeout(timeout);
  }, [controlsVisible]);

  // Mostrar controles ao mover mouse/tocar DENTRO do player
  React.useEffect(() => {
    const showControls = () => setControlsVisible(true);
    const node = playerContainerRef.current;
    if (node) {
      node.addEventListener('mousemove', showControls);
      node.addEventListener('touchstart', showControls);
    }
    return () => {
      if (node) {
        node.removeEventListener('mousemove', showControls);
        node.removeEventListener('touchstart', showControls);
      }
    };
  }, []);

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (playing) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(err => {
      });
    }
  };

  return (
    <div ref={playerContainerRef} className={`video-player-container pf-embed-player${controlsVisible ? ' controls-visible' : ''}`}>
      <div className="pf-video-wrapper">
        {isLoading && (
          <div className="loader"><div></div></div>
        )}
        <video
          ref={videoRef}
          src={ensureMp4Extension(src)}
          controls={false}
          poster={posterPath || "https://cdn.playflix.fun/assets/poster-default.jpg"}
          onLoadStart={() => { setIsLoading(true); }}
          onWaiting={() => { setIsLoading(true); }}
          onCanPlay={() => { setIsLoading(false); }}
          onPlaying={() => { setIsLoading(false); }}
          onLoadedData={() => { setIsLoading(false); }}
          onPlay={() => {
            setPlaying(true);
          }}
          onPause={() => {
            setPlaying(false);
          }}
          onTimeUpdate={(e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
            const time = e.currentTarget.currentTime;
            setCurrent(time);
          }}
          onLoadedMetadata={(e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
            const duration = e.currentTarget.duration;
            setDuration(duration);
          }}
          onProgress={(e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
            const video = e.currentTarget;
            if (video.buffered.length > 0) {
              const buf = video.buffered.end(video.buffered.length - 1);
              setBuffer(buf);
            }
          }}
          onError={(e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
            const video = e.currentTarget;
          }}
          style={{ width: "100%", borderRadius: 16, background: "#000" }}
        />
        <div className="touch-controls">
          <div
            className="touch-zone touch-zone-left"
            onClick={() => {
              if (videoRef.current && Number.isFinite(videoRef.current.currentTime)) {
                const newTime = Math.max(0, videoRef.current.currentTime - 10);
                videoRef.current.currentTime = newTime;
                setCurrent(newTime);
                setShowFeedback('rewind');
                setTimeout(() => setShowFeedback(null), 500);
              }
            }}
          >
            {showFeedback === 'rewind' && (
              <div className="touch-feedback">
                <svg width="48" height="48" viewBox="0 0 48 48"><polygon points="32,12 16,24 32,36" fill="#fff"/><polygon points="24,12 8,24 24,36" fill="#fff"/></svg>
                <span>-10s</span>
              </div>
            )}
          </div>
          <div
            className="touch-zone touch-zone-right"
            onClick={() => {
              if (videoRef.current && Number.isFinite(videoRef.current.currentTime) && Number.isFinite(videoRef.current.duration)) {
                const newTime = Math.min(videoRef.current.duration, videoRef.current.currentTime + 10);
                videoRef.current.currentTime = newTime;
                setCurrent(newTime);
                setShowFeedback('forward');
                setTimeout(() => setShowFeedback(null), 500);
              }
            }}
          >
            {showFeedback === 'forward' && (
              <div className="touch-feedback">
                <svg width="48" height="48" viewBox="0 0 48 48"><polygon points="16,12 32,24 16,36" fill="#fff"/><polygon points="24,12 40,24 24,36" fill="#fff"/></svg>
                <span>+10s</span>
              </div>
            )}
          </div>
        </div>

        {/* Botão central Netflix-style */}
       
<button
  className={`center-play-button${playing ? ' playing' : ''}`}
  onClick={handlePlayPause}
  tabIndex={0}
  aria-label={playing ? 'Pausar' : 'Reproduzir'}
  style={{ pointerEvents: 'all' }}
>
  {playing ? (
    <svg width="36" height="36" viewBox="0 0 32 32"><rect x="8" y="7" width="5" height="18" rx="2" fill="#fff"/><rect x="19" y="7" width="5" height="18" rx="2" fill="#fff"/></svg>
  ) : (
    <svg width="36" height="36" viewBox="0 0 32 32"><polygon points="8,7 26,16 8,25" fill="#fff"/></svg>
  )}
</button>
      </div>
      <div className="netflix-controls">
        {/* Botão play/pause à esquerda */}
        <button
          className="netflix-control-button"
          onClick={handlePlayPause}
          aria-label={playing ? 'Pausar' : 'Reproduzir'}
        >
          {playing ? (
            <svg width="24" height="24" viewBox="0 0 32 32"><rect x="8" y="7" width="5" height="18" rx="2" fill="#fff"/><rect x="19" y="7" width="5" height="18" rx="2" fill="#fff"/></svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 32 32"><polygon points="8,7 26,16 8,25" fill="#fff"/></svg>
          )}
        </button>
        {/* Tempo atual */}
        <span className="time-display">{formatTime(current)}</span>
        {/* Barra de progresso customizada */}
        <div className="progress-container" style={{ flex: 1, margin: '0 12px', position: 'relative' }}
          onClick={e => {
            if (!videoRef.current || !Number.isFinite(duration) || duration <= 0) return;
            
            const rect = (e.target as HTMLDivElement).getBoundingClientRect();
            const clickX = (e as React.MouseEvent).clientX - rect.left;
            const percent = Math.min(Math.max(clickX / rect.width, 0), 1);
            const newTime = percent * duration;
            
            if (Number.isFinite(newTime) && newTime >= 0) {
              videoRef.current.currentTime = newTime;
              setCurrent(newTime);
            }
          }}
        >
          <div
            className="progress-bar"
            style={{ width: duration ? `${(current / duration) * 100}%` : '0%' }}
          />
        </div>
        {/* Duração total */}
        <span className="time-display">{formatTime(duration)}</span>
        {/* Botão de fullscreen */}
        <button
          className="netflix-control-button"
          onClick={async () => {
            if (!document.fullscreenElement) {
              // Entrar em fullscreen
              const container = videoRef.current?.parentElement?.parentElement;
              if (container && container.requestFullscreen) {
                await container.requestFullscreen();
                // Tentar rotacionar para landscape
                if (screen.orientation && (screen.orientation as any).lock) {
                  try {
                    await (screen.orientation as any).lock('landscape');
                  } catch (e) {
                    console.warn('Falha ao rotacionar tela:', e);
                  }
                }
              }
            } else {
              // Sair do fullscreen
              document.exitFullscreen();
              // Tentar desbloquear orientação
              if (screen.orientation && (screen.orientation as any).unlock) {
                try {
                  (screen.orientation as any).unlock();
                } catch (e) {
                  // Alguns browsers não suportam unlock
                }
              }
            }
          }}
          aria-label="Alternar tela cheia"
        >
          {document.fullscreenElement ? (
            // Ícone sair do fullscreen
            <svg width="24" height="24" viewBox="0 0 32 32"><path fill="#fff" d="M10 22H6v-4h2v2h2v2zm0-12H8V8h4v2H10v2zm12 0h-2V8h4v4h-2V10zm0 12v-2h2v-2h2v4h-4z"/></svg>
          ) : (
            // Ícone entrar em fullscreen
            <svg width="24" height="24" viewBox="0 0 32 32"><path fill="#fff" d="M8 8h6V6H6v8h2V8zm10-2v2h6v6h2V6h-8zm6 18h-6v2h8v-8h-2v6zm-10 2v-2H8v-6H6v8h8z"/></svg>
          )}
               </button>
        {/* Botão de PiP */}
        {document.pictureInPictureEnabled && (
          <button
            className="netflix-control-button"
            onClick={async () => {
              if (!videoRef.current) return;
              if (document.pictureInPictureElement) {
                await document.exitPictureInPicture();
              } else {
                try {
                  await videoRef.current.requestPictureInPicture();
                } catch (err) {
                  console.error('Erro ao ativar PiP:', err);
                }
              }
            }}
            aria-label="Alternar Picture-in-Picture"
          >
            {document.pictureInPictureElement ? (
              // Ícone PiP ativo
              <svg width="24" height="24" viewBox="0 0 32 32"><rect x="3" y="6" width="26" height="20" rx="3" fill="#fff" fillOpacity="0.25"/><rect x="18" y="17" width="8" height="6" rx="1" fill="#fff"/></svg>
            ) : (
              // Ícone PiP inativo
              <svg width="24" height="24" viewBox="0 0 32 32"><rect x="3" y="6" width="26" height="20" rx="3" fill="#fff" fillOpacity="0.25"/><rect x="18" y="17" width="8" height="6" rx="1" fill="#fff"/></svg>
            )}
          </button>
        )}
        
      </div>
      
    {/* Dialog de retomar progresso */}
    {resumeDialog !== null && (
      <div style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        background: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <div style={{ background: '#181818', color: '#fff', borderRadius: 12, padding: 32, minWidth: 320, boxShadow: '0 8px 32px #000b' }}>
          <div style={{ fontSize: 20, marginBottom: 16, textAlign: 'center' }}>
            Você parou em <b>{formatTime(resumeDialog)}</b>.<br />Deseja retomar de onde parou?
          </div>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
            <button style={{ background: 'var(--netflix-red)', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 24px', fontSize: 16, cursor: 'pointer', fontWeight: 600 }}
              onClick={() => {
                setResumeAccepted(true);
              }}>
              Retomar
            </button>
            <button style={{ background: '#333', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 24px', fontSize: 16, cursor: 'pointer', fontWeight: 600 }}
              onClick={() => {
                setResumeDialog(null);
                localStorage.removeItem(CACHE_KEY);
                setTimeout(() => {
                  if (videoRef.current) videoRef.current.currentTime = 0;
                  videoRef.current?.play();
                }, 0);
              }}>
              Começar do início
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
  );
};

export default EmbedPlayer;
